import React, { useEffect } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { arrayOf, array, bool, func, node, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import StarRatings from 'react-star-ratings';
import config from '../../config';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  FOR_SALE,
  SIZE_TYPE,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { AvatarMedium, PrimaryButton } from '../../components';

import BookingDatesForm from './BookingDatesForm/BookingDatesForm';
import ProductOrderForm from './ProductOrderForm/ProductOrderForm';
import css from './OrderPanel.module.css';
import { useSelector } from 'react-redux';

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const calculateAverage = reviews => {
  if (reviews?.length === 0) {
    return 0;
  }
  const sum =
    reviews &&
    reviews?.reduce((accumulator, currentValue) => accumulator + currentValue.attributes.rating, 0);
  const average = sum / reviews?.length;
  return average;
};

export const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    isOwnListing,
    unitType,
    onSubmit,
    title,
    author,
    onManageDisableScrolling,
    timeSlots,
    fetchTimeSlotsError,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    isReportModalOpen,
    addToCartAndRemoveFromCart,
    reviews,
    makeAnOfferButton,
    isTransactionPanel,
    transaction,
    currentUser,
    isStripeConnected,
    onFetchStripeDetails,
  } = props;

  const state = useSelector(state => state);
  const { authorData } = state.ListingPage;
  const { sellerShippingAddress = [] } =
    (!!authorData?.id && authorData.attributes.profile.protectedData) || {};

  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;
  const shouldHaveBooking = isNightly || isDaily;
  const { availability } = listing.attributes.publicData;
  const price = listing.attributes.price;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingDatesForm = shouldHaveBooking && hasListingState && !isClosed;
  const { formattedPrice, priceTitle } = priceData(price, intl);
  const { listingPrice, offerPrice, isOfferTransaction, counterOfferDetails = [] } =
    transaction?.attributes?.protectedData || {};
  const lastIndex = counterOfferDetails && counterOfferDetails.length - 1;
  const counterPrice =
    counterOfferDetails[lastIndex] && counterOfferDetails[lastIndex].counterPrice;
  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const currentStock = listing.currentStock?.attributes?.quantity;
  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = true;
  // A closed or sold out listing cannot be bought, offered on or added to the bag.
  const isUnavailable = isClosed || currentStock === 0;

  const { pickupEnabled, shippingEnabled, size, brand, customSize } =
    listing?.attributes?.publicData || {};
  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);

  useEffect(() => {
    if (author?.id?.uuid) {
      onFetchStripeDetails(author?.id?.uuid);
    }
  }, []);

  return (
    <div className={classes}>
      {isTransactionPanel && isOfferTransaction ? (
        <div className={css.priceContainer}>
          <h1 className={css.title}>{title}</h1>
          <div className={css.priceValue} title={priceTitle}>
            <FormattedMessage id="OrderPanel.sizeText" />
            {size === SIZE_TYPE ? customSize : size} {brand && <>| {brand}</>}
          </div>
          <div className={css.priceList}>
            <span className={css.listingPrice}>
              {' '}
              <FormattedMessage id="OrderPanel.listingPrice" values={{ listingPrice }} />
            </span>
            <span className={css.offerPrice}>
              <FormattedMessage id="OrderPanel.offerPrice" values={{ offerPrice }} />{' '}
            </span>
            {counterPrice && (
              <FormattedMessage id="OrderPanel.counterPrice" values={{ counterPrice }} />
            )}
          </div>
        </div>
      ) : (
        <div className={css.priceContainer}>
          <h1 className={css.title}>{title}</h1>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice} {price.currency} | <FormattedMessage id="OrderPanel.sizeText" />
            {size === SIZE_TYPE ? customSize : size} {brand && <>| {brand}</>}
          </div>
          <div className={css.author}>
            <AvatarMedium user={author} className={css.providerAvatar} />
            <div>
              <div className={css.authorName}>{authorDisplayName} </div>
              <div className={css.starRatings}>
                <StarRatings
                  svgIconViewBox="0 0 40 37"
                  svgIconPath="M20 0L26.113 11.5862L39.0211 13.8197L29.891 23.2138L31.7557 36.1803L20 30.4L8.2443 36.1803L10.109 23.2138L0.97887 13.8197L13.887 11.5862L20 0Z"
                  starRatedColor="#ffb802"
                  // starEmptyColor="#ffffff"
                  rating={reviews?.length ? calculateAverage(reviews) : 0}
                  starDimension="25px"
                  starSpacing="2px"
                />
              </div>
            </div>
            {isOwnListing ? null : (
              <div className={css.orderColumnForProductLayout}>
                <p
                  onClick={() => {
                    isReportModalOpen();
                  }}
                  className={css.reportButton}
                >
                  <FormattedMessage id="ListingPage.reportBug" />
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {availability === FOR_SALE ? (
        <>
          {showBookingDatesForm ? (
            <BookingDatesForm
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              submitButtonWrapperClassName={css.bookingDatesSubmitButtonWrapper}
              unitType={unitType}
              onSubmit={onSubmit}
              price={price}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              timeSlots={timeSlots}
              fetchTimeSlotsError={fetchTimeSlotsError}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
            />
          ) : showProductOrderForm ? (
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              onSubmit={onSubmit}
              isStripeConnected={isStripeConnected}
              transaction={transaction}
              price={price}
              currentStock={currentStock}
              pickupEnabled={pickupEnabled}
              shippingEnabled={shippingEnabled}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onContactUser={onContactUser}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              listing={listing}
              currentUser={currentUser}
              isUnavailable={isUnavailable}
              isClosed={isClosed}
            />
          ) : null}{' '}
          {(!sellerShippingAddress?.length > 0 || isUnavailable) && <br />}
          {/* </ModalInMobile> */}
          {isOwnListing || !sellerShippingAddress?.length > 0 || isUnavailable
            ? null
            : addToCartAndRemoveFromCart}
          {isOwnListing || !sellerShippingAddress?.length > 0 || isUnavailable
            ? null
            : makeAnOfferButton}
        </>
      ) : (
        <PrimaryButton>
          <FormattedMessage id="ProductOrderForm.soldButton" />
        </PrimaryButton>
      )}
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  subTitle: null,
  unitType: config.lineItemUnitType,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  unitType: propTypes.lineItemUnitType,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

// Unwrapped component for tests.
export { OrderPanel as OrderPanelComponent };

export default compose(withRouter, injectIntl)(OrderPanel);
